import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import dayjs from 'dayjs';
import { DeleteOrderAction, getpaginatePOAction, ModifyOrderAction } from './API/Action';
import CustomChip from '../Components/CustomChip';
import { toast } from 'react-toastify';
import { getVerificationStatusLabel, ORDER_STATUS } from '../../utils/settings';
import StatusBadge from '../../v2/MUI_custom_component/Badge/StatusBadge';
import { CustomButton } from '../../v2/MUI_custom_component';
import { useDispatch, useSelector } from 'react-redux';
import EditPOModal from './Components/EditPOModal';
import { useNavigate } from 'react-router-dom';
import ViewAttachedDP from './ViewAttachedDP';
import HumbeeModal from '../../v2/MUI_custom_component/Modal/HumbeeModal';
import DeleteSvg from '../../v3/Illustrration/components/DeleteSvg';
import IllustrationWrapper from '../Illustrration/IllustrationWrapper';
import NoDataFoundSvg from '../Illustrration/components/NoDataFoundSvg';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import { initDP, resetDP, resetDpRouteSource, resetSelectedShipToParty, setMoQData } from '../DP/CreateDP/reducer/DpActions';
import StickyEntity from '../Common/Entity/StickyEntity';
import { MODULE_PERMISSION_ENUM } from '../Sidebar/Enum';
import NoAccess from '../Common/NoAccess/NoAccess';
const PAGE_SIZE = 10;

const POList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [dateFilter, setDateFilter] = useState('');
    const [openEditPOModal, setOpenEditPOModal] = useState(false);
    const [openDeletePOModal, setOpenDeletePOModal] = useState(false);
    const [poDetails, setPoDetails] = useState({});
    const [openViewAttachedDP, setOpenViewAttachedDP] = useState(false);
    const isMobile = useMediaQuery('(max-width: 1200px)');

    const {
        selectedSupplierData,
        selectedSupplier: selectedManufacturer,
        selectedEntity: selectedCompanyEntity,
        selectedEntityInfo,
    } = useSelector(
        (state) => state.entityReducer
    );
    const hasAccess = selectedEntityInfo?.has_access;
    const getStartEndDate = useCallback(() => {
        const now = dayjs();
        switch (dateFilter) {
        case '1month':
            return {
            startDate: now.subtract(1, 'month').toDate(),
            endDate: now.toDate()
            };
        case '3months':
            return {
            startDate: now.subtract(3, 'month').toDate(),
            endDate: now.toDate()
            };
        default:
            return { startDate: null, endDate: null };
        }
    }, [dateFilter]);

    // Custom fetch function for the infinite scroll hook
    const fetchPOList = useCallback(async (page, pageSize) => {
        const config = new URLSearchParams();
        config.append('f', 'id$gte$0');
        config.append('o', 'id$desc');
        config.append('page', page.toString());
        config.append('page_size', pageSize.toString());
        config.append('is_buyer', 'true');
    
        if (selectedCompanyEntity) {
            config.append('f', `buyer_esi_id$eq$${selectedCompanyEntity}`);
        }
        if (selectedStatus.length > 0) {
            config.append('f', `final_status$in$${selectedStatus.join('~')}`);
        }
        
        const { startDate, endDate } = getStartEndDate();
        if (startDate && endDate) {
            config.append('f', `documented_at$gte$${dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm:ssZ')}`);
            config.append('f', `documented_at$lte$${dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm:ssZ')}`);
        }
        
        try {
            const result = await getpaginatePOAction(config);
            if (result.error) {
                toast.error(result.message || 'Failed to fetch purchase orders');
            }
            return result;
        } catch (error) {
            toast.error('Failed to fetch purchase orders');
            return { error: true, data: [] };
        }
    }, [selectedCompanyEntity, selectedStatus, getStartEndDate]);
    const {
        data: poList,
        loading,
        hasMore,
        refetchData,
        MoQData,
    } = useInfiniteScroll({
        fetchFunction: fetchPOList,
        pageSize: PAGE_SIZE,
        dependencies: (selectedCompanyEntity && hasAccess !== false) ? [selectedCompanyEntity, selectedStatus, dateFilter] : []
    });

    // Dispatch MoQData to Redux when it changes
    useEffect(() => {
        if (MoQData) {
            dispatch(setMoQData(MoQData));
        }
    }, [MoQData, dispatch]);

    
    
    const statusOptions = ['pending', 'approved', 'partially_completed', 'completed', 'cancelled'];
    const statusLabels = {
        pending: 'Pending',
        approved: 'Approved',
        partially_completed: 'Partially Completed', 
        completed: 'Completed',
        cancelled: 'Cancelled'
    };
    const dateFilters = [
        { value: '3months', label: 'Past 3 months' },
        { value: '1month', label: 'Past 1 month' },
    ];
    const handleStatusFilter = (status) => {
        const newSelectedStatus = selectedStatus.includes(status)
        ? selectedStatus.filter(s => s !== status)
        : [...selectedStatus, status];
        setSelectedStatus(newSelectedStatus);
    };
    
    // Helper function to sort items with selected ones first
    const sortBySelected = (items, isSelectedFn) => {
        return items.sort((a, b) => {
            const aSelected = isSelectedFn(a);
            const bSelected = isSelectedFn(b);
            return bSelected - aSelected; // Selected items first
        });
    };
    const OnPressEditPO = (po) => {
        setPoDetails(po);
        setOpenEditPOModal(true);
    };
    const OnPressCreateDP = (po) => {
        dispatch(resetDP());
        dispatch(resetDpRouteSource())
        dispatch(resetSelectedShipToParty());
        dispatch(initDP(po.humbee_po_number, po?.po_remaining_qty));
        dispatch(setMoQData(MoQData));
        navigate('/create-dp', { state: {
             humbee_po_number: po.humbee_po_number,
             po_remaining_qty: po?.po_remaining_qty || null,
            }
        });
    };
    const onViewDPAttached = (po) => {
        setPoDetails(po);
        setOpenViewAttachedDP(true);
    };
    const onCancelPO = (po) => {
        setPoDetails(po);
        setOpenDeletePOModal(true);
    };
    const handleDeletePO = async () => {
        const config = new URLSearchParams();
        config.append('order_booking_id', poDetails.humbee_po_number);
        config.append('user_esi_id', selectedCompanyEntity,);
        const { error, message } = await DeleteOrderAction(config);
        if (!error) {
            toast.success(message);
            setOpenDeletePOModal(false);
            refetchData();
        } else {
            toast.error(message);
        }
    };
    
    const handleEditOrder = async (editData) => {
        const payload = {
            order_booking_id: editData.poDetails?.humbee_po_number,
            user_po_number: editData.userPONumber,
            user_esi_id: selectedCompanyEntity,
            product_list: [
                    {
                        product_id: editData.poDetails.quantity_info[0].product_id,
                        quantity: {
                            quantity_in_metric_ton: Number(editData.quantity),
                            multiplier: editData.poDetails.quantity_info[0].mulitplier_id,
                        },
                    },
                ],
        };
        const { error, message } = await ModifyOrderAction(payload, selectedCompanyEntity);
        if (!error) {
            toast.success(message);
        }else{
            toast.error(message);
        }
        
        setOpenEditPOModal(false);
        refetchData();
    };
    const showHistory = useSelector(state => state.entityReducer.show_history);
    const renderPOItem = (po, index) => (
        <Box key={`${po.id}-${index}`} sx={{
            p: 2,
            backgroundColor: '#fff',
            borderRadius: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
        }}>
                <Box sx={{ display: 'flex', gap:{xs: 0.5, md: 2}, flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }}}>
                    <Typography variant="bodyL" sx={{ borderRight: {md:'1px solid #D9D9D9'}, minWidth: '110px',}}>
                        {dayjs(po.booking_date).format('DD MMM YYYY')}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                            <Typography variant="bodyL">
                                {po.user_po_number || '-'}
                            </Typography>
                            <StatusBadge 
                                label={getVerificationStatusLabel(po.status)} 
                                status={po.status} 
                                size={isMobile? "small" : "medium"}
                            />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, width: '100%', gap: 2}}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'row', },
                            width: '100%',
                            gap: 2,
                            ml: { md: '125px' },
                        }}
                    >
                        {po.quantity_info && (
                            <Box sx={{ gap: 0.5, display: 'flex', flexDirection: 'column', width: { xs: '44%', md: '50%' } }}>
                                {po.quantity_info.map(item => (
                                    <Box key={item.product_id} sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="labelM" sx={{ minWidth: { xs: '70px', md: '100px'}, color: '#666' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="p3" sx={{ color: '#333' }} >
                                            {item.value} {item.unit}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {po.quantity_info && po.price_info && (
                            <Box sx={{ width: '1px', backgroundColor: '#D9D9D9', display: { xs: 'block', md: 'none' } }} />
                        )}
                        {po.price_info && (
                            <Box sx={{ gap: 0.5, display: 'flex', flexDirection: 'column' }}>
                                {po.price_info.map(item => (
                                    <Box key={item.product_id} sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="labelM" sx={{ minWidth: { xs: '60px', sm: '90px', md: '100px'}, color: '#666' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography variant="p3" sx={{ color: item.type == 'success' ? '#008000': '#333'}} >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: { md: 'flex-end' }, minWidth: { md: '200px' }}}>
                        {po.status !== ORDER_STATUS.CANCELLED && po.status !== ORDER_STATUS.COMPLETED && (
                            <CustomButton 
                                variant="filled" 
                                size={isMobile? "small": "medium"}
                                color={'primary'}
                                onClick={() =>
                                    po.status === ORDER_STATUS.PENDING
                                        ? OnPressEditPO(po)
                                        : OnPressCreateDP(po)
                                }
                                label={po.status === 'PENDING' ? 'Edit PO' : 'Create DP'}
                                disabled={showHistory}
                            />
                        )}
                        {po.status !== ORDER_STATUS.CANCELLED && po.status !== ORDER_STATUS.APPROVED &&(
                            <CustomButton
                                variant='text'
                                color={'primary'}
                                size={isMobile? "small": "medium"}
                                onClick={() =>
                                    po.status === ORDER_STATUS.PENDING
                                    ? onCancelPO(po)
                                    : onViewDPAttached(po)
                                }
                                disabled={showHistory && po.status === ORDER_STATUS.PENDING}
                                label={po.status === ORDER_STATUS.PENDING ? 'Cancel PO' : 'View Attached DP'}
                            />
                        )}
                    </Box>
                </Box>
        </Box>
    );
    const isEntityEnabled = selectedSupplierData?.entity_enabled;
    return (
        <>
            <StickyEntity 
                module_name={MODULE_PERMISSION_ENUM.PURCHASE_ORDER}
            />
            {hasAccess === false ?(
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '60vh',
                    }}
                >
                    <NoAccess />
                </Box>
            ) : (
            <>
            <Box sx={{ 
                mt: {xs : isEntityEnabled ? '96px': 0, md: 0 },
                display: 'flex', 
                gap: 1, 
                overflowX: 'scroll', 
                pt: {xs: 2, md: 2 },
                pb: {xs: '2px', md: 2},
                px: 2,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
            }}>
                {[
                    ...statusOptions.map((status) => ({
                        key: status,
                        label: statusLabels[status],
                        selected: selectedStatus.includes(status),
                        onClick: () => handleStatusFilter(status),
                        onDelete: selectedStatus.includes(status) ? () => handleStatusFilter(status) : undefined,
                    })),
                    ...dateFilters.map((filter) => ({
                        key: filter.value,
                        label: filter.label,
                        selected: dateFilter === filter.value,
                        onClick: () => setDateFilter(filter.value),
                        onDelete: dateFilter === filter.value ? () => setDateFilter('') : undefined,
                    }))
                ]
                    .sort((a, b) => b.selected - a.selected) 
                    .map((chip) => (
                        <CustomChip
                            key={chip.key}
                            label={chip.label}
                            onClick={chip.onClick}
                            onDelete={chip.onDelete}
                            selected={chip.selected}
                            variant={chip.selected ? "filled" : "outlined"}
                        />
                    ))
                }
            </Box>
            <Box sx={{ p: { xs: 2, md: 2 }, pt: { xs: 2, md: 0 } }}>
                <Box 
                    sx={{ 
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'column',
                    }}
                >
                
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2}}>
                        {poList.length > 0 ? (
                            <>
                                {poList.map((po, index) => renderPOItem(po, index))}
                                {(loading || hasMore) && poList.length > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                                {!hasMore && poList.length > 0 && !loading && (
                                    <Box sx={{ textAlign: 'center', p: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No more data available
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        ) : (
                            !loading && (
                                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                    <IllustrationWrapper
                                        illustration={<NoDataFoundSvg />}
                                        title="No Purchase Orders found. "
                                        subtitle={ selectedStatus.length > 0 ? "Try adjusting your filters." :"No purchase orders available yet. Create a PO to get started."}
                                    />
                                    { !selectedStatus.length > 0  && (
                                        <CustomButton
                                            variant="filled"
                                            color="primary"
                                            size={isMobile? "small": "medium"}
                                            label="Create Purchase Order"
                                            disabled={showHistory}
                                            onClick={() => navigate('/create-po')}
                                            sx={{ width: { xs: '100%', md: 'auto' } }}
                                        />
                                    )}
                                </Box>
                            )
                        )}
                    </Box>
                    {loading && poList.length === 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
                <EditPOModal
                    open={openEditPOModal}
                    onClose={() => setOpenEditPOModal(false)}
                    poDetails={poDetails}
                    selectedSupplierData={selectedSupplierData}
                    onSave={handleEditOrder}
                    loading={loading}
                />
                <HumbeeModal
                    open={openDeletePOModal}
                    onClose={() => setOpenDeletePOModal(false)}
                    onConfirm={handleDeletePO}
                    loading={loading}
                    title="Are you sure you want to cancel PO?"
                    subtitle="This action cannot be undone. All data would be lost."
                    confirmLabel = "Yes"
                    cancelLabel = "No"
                    illustration={<DeleteSvg />}
                    width={{ xs: 400 , md:  600 }}
                />
                <ViewAttachedDP
                    open={openViewAttachedDP}
                    onClose={() => setOpenViewAttachedDP(false)}
                    poDetails={poDetails}
                />
            </Box>
            </>
            )}
        </>
    );
};

export default POList;